import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import Image from '@jwp/ott-ui-react/src/components/Image/Image';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import Lock from '@jwp/ott-theme/assets/icons/lock.svg?react';
import Play from '@jwp/ott-theme/assets/icons/play.svg?react';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import InfiniteScrollLoader from '@jwp/ott-ui-react/src/components/InfiniteScrollLoader/InfiniteScrollLoader';
import createInjectableComponent from '@jwp/ott-ui-react/src/modules/createInjectableComponent';
import { getScrollParent } from '@jwp/ott-ui-react/src/utils/dom';
import classNames from 'classnames';

import styles from './EpisodeList.module.scss';

export const EpisodeListIdentifier = Symbol(`EPISODE_LIST`);

const INITIAL_RENDER_COUNT = 10;
const RENDER_COUNT_STEP = 10;

export type Props = {
  playlist: Playlist;
  watchHistory: { [key: string]: number };
  areEpisodesEntitled: { [mediaid: string]: boolean };
  isLoading: boolean;
  isEpisodesFetching: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  onEpisodeClick: (item: PlaylistItem) => void;
  renderEpisodeMetadata: (item: PlaylistItem) => React.ReactNode;
};

function EpisodeList({
  playlist,
  watchHistory,
  isLoading,
  isEpisodesFetching,
  areEpisodesEntitled,
  hasMore: hasMoreToLoad,
  loadMore,
  onEpisodeClick,
  renderEpisodeMetadata,
}: Props) {
  const { t } = useTranslation(['common', 'video']);
  const breakpoint = useBreakpoint();
  const scrollParent = getScrollParent();
  const isSmallScreen = breakpoint === Breakpoint.xs;
  const [renderCount, setRenderCount] = useState(INITIAL_RENDER_COUNT);
  const hasMoreToRender = renderCount < playlist.playlist.length;

  const handleLoadMore = () => {
    if (hasMoreToRender) {
      setRenderCount((current) => current + RENDER_COUNT_STEP);
      return;
    }
    if (hasMoreToLoad && !isEpisodesFetching) {
      loadMore?.();
      setRenderCount((current) => current + RENDER_COUNT_STEP);
    }
  };

  useEffect(() => {
    // reset row count when the playlist changes
    setRenderCount(INITIAL_RENDER_COUNT);
  }, [playlist.feedid]);

  return (
    <InfiniteScroll
      loadMore={handleLoadMore}
      hasMore={hasMoreToLoad}
      loader={<InfiniteScrollLoader key="loader" />}
      getScrollParent={() => scrollParent}
      useWindow={scrollParent === document.documentElement || scrollParent === document.body}
    >
      <ol className={styles.episodeList}>
        {playlist.playlist.slice(0, renderCount).map((playlistItem) => {
          const title = playlistItem.episodeNumber ? `${playlistItem.episodeNumber}. ${playlistItem.title}` : playlistItem.title;
          const isLockedEpisode = !areEpisodesEntitled[playlistItem.mediaid];
          const progress = watchHistory[playlistItem.mediaid];

          const episodeSummary = playlistItem.synopsis ? (
            /* Synopsis is shown entirely */
            <p className={styles.synopsis}>{playlistItem.synopsis as string}</p>
          ) : (
            /* Description is truncated using CSS */
            <MarkdownComponent className={styles.description} tag="div" markdownString={playlistItem.description} />
          );

          return (
            <li key={playlistItem.mediaid}>
              <button
                type="button"
                onClick={() => onEpisodeClick(playlistItem)}
                className={styles.button}
                aria-label={t('common:play_item', { title: playlistItem.title })}
              >
                <div className={styles.episode}>
                  <div className={styles.poster}>
                    <Image className={styles.posterImage} image={playlistItem.cardImage} width={320} alt="" />
                    {!isLoading && (
                      <div className={styles.posterMeta}>
                        <div className={styles.posterTags}>
                          <div className={classNames(styles.play, { [styles.hidden]: isLockedEpisode })}>
                            <Icon icon={Play} />
                          </div>
                          <div className={styles.posterTagsRight}>
                            {isLockedEpisode && (
                              <div className={`${styles.tag} ${styles.lock}`} aria-label={t('common:card_lock')} role="img">
                                <Icon icon={Lock} />
                              </div>
                            )}
                            {playlistItem.duration && (
                              <div className={styles.tag}>
                                {formatDurationTag(playlistItem.duration, { minutesAbbreviation: t('common:abbreviation.minutes') })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {progress ? (
                      <div className={styles.progressContainer}>
                        <div className={styles.progressBar} style={{ width: `${Math.round(progress * 100)}%` }} />
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.metadataContainer}>
                    <h3 className={styles.title}>{title}</h3>
                    <div className={styles.metaContainer}>{renderEpisodeMetadata(playlistItem)}</div>
                    {isSmallScreen ? null : episodeSummary}
                  </div>
                </div>
                {isSmallScreen ? episodeSummary : null}
              </button>
            </li>
          );
        })}
      </ol>
    </InfiniteScroll>
  );
}

export default createInjectableComponent(EpisodeListIdentifier, EpisodeList);
